'use client';

import React, { useState } from 'react';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface LoginFormProps {
  onSwitchToSignup: () => void;
}

export default function LoginForm({ onSwitchToSignup }: LoginFormProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    setIsLoading(true);
    try {
      const supabase = createClient();
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password: password,
      });
      if (error) throw error;

      // Try to get role from user_profiles first, fall back to user_metadata
      let role = data.user?.user_metadata?.role ?? '';
      try {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('id', data.user.id)
          .single();
        if (profile?.role) role = profile.role;
      } catch {
        // use metadata role as fallback
      }

      const normalizedRole = role?.toUpperCase?.() ?? '';
      toast.success('Welcome back to TubeNexa!');
      if (normalizedRole === 'SUPER_ADMIN' || normalizedRole === 'ADMIN') {
        window.location.href = '/admin';
      } else {
        window.location.href = '/artist-dashboard';
      }
    } catch (err: any) {
      const msg = err?.message ?? '';
      if (
        msg.toLowerCase().includes('invalid') ||
        msg.toLowerCase().includes('credentials') ||
        msg.toLowerCase().includes('password')
      ) {
        setErrorMessage('Invalid email or password. Please try again.');
      } else {
        setErrorMessage(msg || 'Sign in failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-1" style={{ color: 'var(--foreground)' }}>
          Welcome back
        </h2>
        <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
          Sign in to your TubeNexa artist account
        </p>
      </div>

      <form onSubmit={handleSignIn} autoComplete="off" className="space-y-5">
        {/* Error message */}
        {errorMessage && (
          <div
            className="text-sm px-3 py-2 rounded-lg"
            style={{ background: 'var(--danger-subtle, rgba(239,68,68,0.1))', color: 'var(--danger)' }}
          >
            {errorMessage}
          </div>
        )}

        {/* Email */}
        <div>
          <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--secondary-foreground)' }}>
            Email address
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="off"
            className="input-field"
            placeholder="you@example.com"
          />
        </div>

        {/* Password */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-sm font-medium" style={{ color: 'var(--secondary-foreground)' }}>
              Password
            </label>
            <button
              type="button"
              className="text-xs hover:underline"
              style={{ color: 'var(--primary-light)' }}
            >
              Forgot password?
            </button>
          </div>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
              minLength={6}
              className="input-field pr-10"
              placeholder="••••••••••"
            />
            <button
              type="button"
              onClick={() => setShowPassword((p) => !p)}
              className="absolute right-3 top-1/2 -translate-y-1/2 btn-ghost p-0"
              style={{ color: 'var(--muted-foreground)' }}
            >
              <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
            </button>
          </div>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full"
          style={{ height: '44px' }}
        >
          {isLoading ? (
            <>
              <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
              Signing in...
            </>
          ) : (
            'Sign In'
          )}
        </button>
      </form>

      <p className="text-center text-sm mt-6" style={{ color: 'var(--muted-foreground)' }}>
        Don&apos;t have an account?{' '}
        <button
          type="button"
          onClick={onSwitchToSignup}
          className="font-semibold hover:underline"
          style={{ color: 'var(--primary-light)' }}
        >
          Create one free
        </button>
      </p>

      <style jsx global>{`
        /* Strip yellow browser autofill background and keep it matching the dark inputs */
        input:-webkit-autofill,
        input:-webkit-autofill:hover, 
        input:-webkit-autofill:focus, 
        input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 30px #13131a inset !important;
          -webkit-text-fill-color: #ffffff !important;
          transition: background-color 5000s ease-in-out 0s;
        }
      `}</style>
    </div>
  );
}